"use client";

import Link from "next/link";
import { useSyncExternalStore } from "react";

import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";

const EMPTY_SUBSCRIBE = () => () => {};

// Gates browser-only UI so it doesn't render during SSR and flash before
// localStorage is read.
function useHasMounted(): boolean {
  return useSyncExternalStore(
    EMPTY_SUBSCRIBE,
    () => true,
    () => false,
  );
}


export function ConsentBanner() {
  const { hasDecided, acceptAll, rejectAll, openSettings } = useConsent();
  const isEmbedded = useIsEmbedded();
  // Consent lives in localStorage, which is client-only — during SSR and
  // the first hydration pass `hasDecided` is always false, so without
  // this gate the banner flashes for ~1 frame even for returning users
  // who'd already accepted. Waiting for client mount lets the store's
  // real client snapshot resolve before we render anything.
  const hasMounted = useHasMounted();

  if (!hasMounted) return null;
  if (hasDecided || isEmbedded) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 md:bottom-6 md:px-6 md:pb-0"
    >
      <div className="mx-auto flex max-w-[560px] flex-col gap-5 rounded-[20px] bg-purple-05 px-5 py-5 text-white shadow-[0_24px_60px_-20px_rgba(13,10,44,0.45)] md:gap-6 md:px-7 md:py-6">
        <div className="flex max-w-[460px] flex-col gap-2">
          <h2 className="text-body font-bold leading-tight tracking-[-0.01em] md:text-h6">
            Your choices regarding cookies on this site
          </h2>
          <p className="text-button leading-snug text-white/85">
            We use cookies to optimise site functionality and give you the
            best possible experience.{" "}
            <Link
              href="/cookie-policy"
              className="underline underline-offset-2 hover:text-purple-01"
            >
              Visit our Cookie Policy
            </Link>{" "}
            to learn more.
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <button
            type="button"
            onClick={acceptAll}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-lg bg-white px-5 text-button font-bold text-purple-05 transition-colors hover:bg-purple-01"
          >
            Allow All
          </button>
          <button
            type="button"
            onClick={rejectAll}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-lg border border-white/40 px-5 text-button font-bold text-white transition-colors hover:bg-white/10"
          >
            Use necessary cookies only
          </button>
          <button
            type="button"
            onClick={openSettings}
            className="mt-3 inline-flex cursor-pointer items-center text-button font-extrabold leading-[1.054] text-white underline-offset-4 hover:underline sm:mt-0 sm:ml-auto"
          >
            Manage Cookies
          </button>
        </div>
      </div>
    </div>
  );
}
